import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import LanguageToggle from '../../components/LanguageToggle';
import { UNION_STAFF_ROLE } from '../../utils/resolveUnionId';
import VocationWorksLogo from '../../components/VocationWorksLogo';

const NAVY     = '#0D1B2A';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';
const BG       = '#F5F6F8';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

const inp = {
  width: '100%', padding: '11px 13px', fontSize: '0.95rem',
  border: '1.5px solid #E2E6EB', borderRadius: '8px',
  color: NAVY, outline: 'none', backgroundColor: WHITE, fontFamily: FONT_BODY,
};
const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '5px' };
const fld = { marginBottom: '1.1rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.1rem' };
const card = {
  backgroundColor: WHITE, borderRadius: '16px',
  boxShadow: '0 2px 24px rgba(0,0,0,0.07)',
  padding: '2rem', marginBottom: '1.25rem',
};

function Shell({ children }) {
  const navigate = useNavigate();
  return (
    <div style={{ fontFamily: FONT_BODY, backgroundColor: BG, minHeight: '100dvh' }}>
      <nav style={{
        position: 'sticky', top: 0, zIndex: 200, height: '60px', backgroundColor: NAVY,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2rem',
      }}>
        <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <VocationWorksLogo variant="reversed" size={36} />
        </div>
        <LanguageToggle />
      </nav>
      <div style={{ maxWidth: '520px', margin: '0 auto', padding: '2.5rem 1.25rem' }}>
        {children}
      </div>
    </div>
  );
}

// Join page for a union_staff invite -- a direct mirror of EmployerJoin.jsx (join an
// *existing* union, no new union created). Validity is only known once
// redeem_union_staff_invite() runs post-signup; same precedent as EmployerJoin: no rollback
// of the just-created auth account on a failed redemption, just the "invalid invite" state.
export default function UnionJoin() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') || '';

  const [form, setForm] = useState({ fullName: '', email: '', password: '', confirmPassword: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState('');
  const [invalid, setInvalid] = useState(false);
  // redeem_union_staff_invite() returns a specific, human-readable reason for every failure
  // case (bad/used code, already staff for a union) -- carried through as-is.
  const [invalidReason, setInvalidReason] = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!code) { setInvalid(true); return; }
    if (!form.fullName.trim() || !form.email.trim() || !form.password || !form.confirmPassword) {
      setError(t('union.signup.step1.requiredFieldsAlert'));
      return;
    }
    if (form.password.length < 8) {
      setError(t('union.signup.step1.passwordLengthAlert'));
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError(t('employer.signup.step1.passwordMismatchAlert'));
      return;
    }

    setSubmitting(true);

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: { data: { full_name: form.fullName, role: UNION_STAFF_ROLE } },
    });
    if (signUpError) { setError(signUpError.message); setSubmitting(false); return; }

    const userId = signUpData?.session?.user?.id || signUpData?.user?.id;
    if (!userId) { setError(t('organization.join.couldNotCreateAccountError')); setSubmitting(false); return; }

    const { data: result, error: rpcError } = await supabase.rpc('redeem_union_staff_invite', { p_invite_code: code, p_display_name: form.fullName.trim() });
    setSubmitting(false);
    if (rpcError || !result?.success) {
      setInvalidReason(result?.error || '');
      setInvalid(true);
      return;
    }

    navigate('/union/dashboard');
  };

  if (invalid) {
    return (
      <Shell>
        <div style={{ ...card, textAlign: 'center' }}>
          <h1 style={{ fontFamily: FONT_HEAD, fontSize: '1.9rem', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.75rem' }}>
            {t('union.join.invalidHeading')}
          </h1>
          <p style={{ fontSize: '0.95rem', color: 'rgba(13,27,42,0.6)', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            {invalidReason || t('union.join.invalidBody')}
          </p>
          <span
            onClick={() => navigate('/login')}
            style={{ color: AMBER, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}
          >
            {t('union.join.goToSignInLink')}
          </span>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: AMBER, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        {t('union.join.eyebrow')}
      </div>

      <div style={card}>
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.1rem', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.35rem' }}>
          {t('union.join.heading')}
        </h1>
        <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.52)', marginBottom: '1.75rem', lineHeight: 1.5 }}>
          {t('union.join.subtitle')}
        </p>

        <div style={fld}>
          <label style={lbl}>{t('organization.join.fullNameLabel')}</label>
          <input style={inp} placeholder={t('employer.signup.step1.fullNamePlaceholder')} value={form.fullName} onChange={e => set('fullName', e.target.value)} />
        </div>

        <div style={fld}>
          <label style={lbl}>{t('worker.signup.step1.emailLabel')}</label>
          <input style={inp} type="email" placeholder={t('employer.signup.step1.workEmailPlaceholder')} value={form.email} onChange={e => set('email', e.target.value)} />
        </div>

        <div style={g2}>
          <div>
            <label style={lbl}>{t('worker.signup.step1.passwordLabel')}</label>
            <input style={inp} type="password" placeholder={t('worker.signup.step1.passwordPlaceholder')} value={form.password} onChange={e => set('password', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>{t('worker.signup.step1.confirmPasswordLabel')}</label>
            <input style={inp} type="password" placeholder={t('worker.signup.step1.confirmPasswordPlaceholder')} value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} />
          </div>
        </div>

        {error && (
          <p style={{ fontSize: '0.85rem', color: '#DC2626', marginBottom: '1rem' }}>{error}</p>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '8px',
            padding: '13px 30px', fontWeight: 700, fontSize: '1rem',
            cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1,
            fontFamily: FONT_BODY, width: '100%', marginTop: '0.5rem',
          }}
        >
          {submitting ? t('organization.join.settingUpButton') : t('union.join.joinButton')}
        </button>
      </div>
    </Shell>
  );
}
